import React from 'react'

import { useGetRecentData } from 'api/api-hooks/datasets-hooks';
import { DatasetDataInfoResponse } from 'api/model/dataset/DatasetDataInfoResponse';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'react-tooltip';
import useChartDialogStore from 'store/chart-dialog-store';
import useDatasetIdentifiersStore from 'store/dataset-identifiers-store';
import { T } from 'services/lang/LanguageService';

type  DeviceDialogContentProps = {
  datasets: string[];
  title: string;
}

const DeviceDialogContent: React.FC<DeviceDialogContentProps> = ({datasets, title}) => {
  const { data, error, isLoading } = useGetRecentData(datasets, {
    options: {
      staleTime: 10000,
    },
  });

  if (isLoading) {
    return (
      <ProgressSpinner
        strokeWidth="4"
        style={{ margin: "auto", width: "100%", overflow: "hidden" }}
      />
    );
  }

  if (error) {
    return (
      <div className="mb-3 text-red-500 text-2xl">
        <i className="pi pi-exclamation-triangle text-2xl pr-2" />
        {T("alert.error")}: {error?.message}
      </div>
    );
  }
  
  return (
    <div>
      <h2>{title}</h2>
      <Divider />
      <div className="flex flex-column gap-3">
        {data?.map(device => <DeviceDataset key={device.identifier} device={device} />)}
      </div>
      <Tooltip id="device-tooltip" delayShow={300} delayHide={0} />
    </div>
  );
}

type DeviceDatasetProps = {
  device: DatasetDataInfoResponse;
}

const DeviceDataset: React.FC<DeviceDatasetProps> = ({device}) => {
  const { openDialog } = useChartDialogStore();
  const { addIdentifier, clear } = useDatasetIdentifiersStore();
  const handleClick = () => {
    clear();
    addIdentifier(device.identifier);
    openDialog();
  };

  let value: number | string = device?.data[0]?.value ?? "N/A";
  value = typeof value === "number" ? value.toFixed(device?.decimalPlaces ?? 2) : value;

  return (
    <div key={device.identifier} className="flex justify-content-between gap-4">
      <div className="flex gap-2 align-items-baseline">
        <p className="device-title">{device.title}</p>
        <i className="pi pi-chart-line" onClick={handleClick} style={{ cursor: "pointer" }} />
      </div>
      <p className="white-space-nowrap"
        data-tooltip-id='device-tooltip'
        data-tooltip-content={new Date(device.data[0]?.key).toLocaleString()}
      >{`${value} ${device?.unit ?? ""}`}</p>
    </div>
  );
}

export default DeviceDialogContent